var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { deepEqual } from '../../../utils/commonUtilities';
import { hasFormErrors, indexFormFields, validateFormField, validateFormSections, } from './form_validation';
var useDynamicForm = function (_a) {
    var sections = _a.sections, initialValues = _a.initialValues, deriveValues = _a.deriveValues, _b = _a.trackDirty, trackDirty = _b === void 0 ? false : _b, lookupHandlers = _a.lookupHandlers, searchHandlers = _a.searchHandlers;
    var _c = useState(initialValues), values = _c[0], setValues = _c[1];
    var _d = useState({}), errors = _d[0], setErrors = _d[1];
    var _e = useState({}), touched = _e[0], setTouched = _e[1];
    var _f = useState({}), files = _f[0], setFiles = _f[1];
    var valuesRef = useRef(values);
    var filesRef = useRef(files);
    var sectionsRef = useRef(sections);
    var initialValuesRef = useRef(initialValues);
    var deriveValuesRef = useRef(deriveValues);
    deriveValuesRef.current = deriveValues;
    valuesRef.current = values;
    filesRef.current = files;
    sectionsRef.current = sections;
    var fieldIndexRef = useRef(null);
    var getFieldConfig = useCallback(function (name) {
        var _a;
        if (((_a = fieldIndexRef.current) === null || _a === void 0 ? void 0 : _a.sections) !== sectionsRef.current) {
            fieldIndexRef.current = {
                sections: sectionsRef.current,
                fieldsByName: indexFormFields(sectionsRef.current),
            };
        }
        return fieldIndexRef.current.fieldsByName.get(name);
    }, []);
    var lookupHandlersRef = useRef(lookupHandlers);
    lookupHandlersRef.current = lookupHandlers;
    var mergeValuesRef = useRef();
    var lastLookedUpValueRef = useRef({});
    var requestIdsRef = useRef({});
    var startRequest = function (key) {
        var _a;
        var requestId = ((_a = requestIdsRef.current[key]) !== null && _a !== void 0 ? _a : 0) + 1;
        requestIdsRef.current[key] = requestId;
        return requestId;
    };
    var isCurrentRequest = function (key, requestId) {
        return requestIdsRef.current[key] === requestId;
    };
    var runFieldLookup = useCallback(function (field) {
        var _a;
        var handler = field.lookupKey
            ? (_a = lookupHandlersRef.current) === null || _a === void 0 ? void 0 : _a[field.lookupKey]
            : undefined;
        if (!handler) {
            return;
        }
        var value = valuesRef.current[field.name];
        var lookupValue = String(value !== null && value !== void 0 ? value : '').trim();
        if (!lookupValue || lastLookedUpValueRef.current[field.name] === lookupValue) {
            return;
        }
        lastLookedUpValueRef.current[field.name] = lookupValue;
        var requestKey = 'lookup:' + field.name;
        var requestId = startRequest(requestKey);
        handler(value, valuesRef.current)
            .then(function (resolvedValues) {
            var _a;
            if (!isCurrentRequest(requestKey, requestId)) {
                return;
            }
            if (resolvedValues) {
                (_a = mergeValuesRef.current) === null || _a === void 0 ? void 0 : _a.call(mergeValuesRef, resolvedValues);
            }
        })
            .catch(function () {
            if (isCurrentRequest(requestKey, requestId)) {
                delete lastLookedUpValueRef.current[field.name];
            }
        });
    }, []);
    var SEARCH_MIN_LENGTH = 3;
    var SEARCH_DEBOUNCE_MS = 600;
    var _g = useState({}), searchOptions = _g[0], setSearchOptions = _g[1];
    var searchHandlersRef = useRef(searchHandlers);
    searchHandlersRef.current = searchHandlers;
    var searchTimersRef = useRef({});
    var lastSearchedTermRef = useRef({});
    useEffect(function () { return function () {
        Object.values(searchTimersRef.current).forEach(clearTimeout);
    }; }, []);
    var handleSearchInput = useCallback(function (name, term) {
        var _a, _b;
        var searchKey = (_a = getFieldConfig(name)) === null || _a === void 0 ? void 0 : _a.searchKey;
        var handler = searchKey ? (_b = searchHandlersRef.current) === null || _b === void 0 ? void 0 : _b[searchKey] : undefined;
        if (!handler) {
            return;
        }
        clearTimeout(searchTimersRef.current[name]);
        var searchTerm = term.trim();
        if (searchTerm.length < SEARCH_MIN_LENGTH) {
            lastSearchedTermRef.current[name] = '';
            setSearchOptions(function (previous) {
                var _a;
                var _b;
                return ((_b = previous[name]) === null || _b === void 0 ? void 0 : _b.length) ? __assign(__assign({}, previous), (_a = {}, _a[name] = [], _a)) : previous;
            });
            return;
        }
        if (lastSearchedTermRef.current[name] === searchTerm) {
            return;
        }
        searchTimersRef.current[name] = setTimeout(function () {
            lastSearchedTermRef.current[name] = searchTerm;
            var requestKey = 'search:' + name;
            var requestId = startRequest(requestKey);
            handler
                .search(searchTerm, valuesRef.current)
                .then(function (options) {
                if (!isCurrentRequest(requestKey, requestId)) {
                    return;
                }
                setSearchOptions(function (previous) {
                    var _a;
                    return (__assign(__assign({}, previous), (_a = {}, _a[name] = options, _a)));
                });
            })
                .catch(function () {
                if (!isCurrentRequest(requestKey, requestId)) {
                    return;
                }
                lastSearchedTermRef.current[name] = '';
                setSearchOptions(function (previous) {
                    var _a;
                    return (__assign(__assign({}, previous), (_a = {}, _a[name] = [], _a)));
                });
            });
        }, SEARCH_DEBOUNCE_MS);
    }, [getFieldConfig]);
    var handleSearchSelect = useCallback(function (name, option) {
        var _a, _b, _c;
        var searchKey = (_a = getFieldConfig(name)) === null || _a === void 0 ? void 0 : _a.searchKey;
        var handler = searchKey ? (_b = searchHandlersRef.current) === null || _b === void 0 ? void 0 : _b[searchKey] : undefined;
        if (!handler) {
            return;
        }
        lastSearchedTermRef.current[name] = String((_c = option.label) !== null && _c !== void 0 ? _c : '').trim();
        var requestKey = 'select:' + name;
        var requestId = startRequest(requestKey);
        handler.select(option, valuesRef.current).then(function (resolvedValues) {
            var _a;
            if (!isCurrentRequest(requestKey, requestId)) {
                return;
            }
            if (resolvedValues) {
                (_a = mergeValuesRef.current) === null || _a === void 0 ? void 0 : _a.call(mergeValuesRef, resolvedValues);
            }
        });
    }, [getFieldConfig]);
    var handleFieldChange = useCallback(function (name, value) {
        var field = getFieldConfig(name);
        var nextValue = (field === null || field === void 0 ? void 0 : field.format) && typeof value === 'string'
            ? field.format(value, valuesRef.current)
            : value;
        setValues(function (previousValues) {
            var _a;
            var _b;
            var nextValues = __assign(__assign({}, previousValues), (_a = {}, _a[name] = nextValue, _a));
            (_b = field === null || field === void 0 ? void 0 : field.resetFields) === null || _b === void 0 ? void 0 : _b.forEach(function (dependentField) {
                nextValues[dependentField] = '';
            });
            return deriveValuesRef.current
                ? __assign(__assign({}, nextValues), deriveValuesRef.current(nextValues)) : nextValues;
        });
        setErrors(function (previousErrors) {
            var _a;
            var clearedKeys = __spreadArray([name], ((_a = field === null || field === void 0 ? void 0 : field.resetFields) !== null && _a !== void 0 ? _a : []), true).filter(function (key) { return previousErrors[key]; });
            if (clearedKeys.length === 0) {
                return previousErrors;
            }
            var nextErrors = __assign({}, previousErrors);
            clearedKeys.forEach(function (key) { return delete nextErrors[key]; });
            return nextErrors;
        });
    }, [getFieldConfig]);
    var handleFieldBlur = useCallback(function (name) {
        setTouched(function (previousTouched) {
            var _a;
            return previousTouched[name] ? previousTouched : __assign(__assign({}, previousTouched), (_a = {}, _a[name] = true, _a));
        });
        var field = getFieldConfig(name);
        if (!field) {
            return;
        }
        var message = validateFormField(field, valuesRef.current);
        setErrors(function (previousErrors) {
            var _a;
            if (((_a = previousErrors[name]) !== null && _a !== void 0 ? _a : '') === message) {
                return previousErrors;
            }
            var nextErrors = __assign({}, previousErrors);
            if (message) {
                nextErrors[name] = message;
            }
            else {
                delete nextErrors[name];
            }
            return nextErrors;
        });
        if (!message) {
            runFieldLookup(field);
        }
    }, [getFieldConfig, runFieldLookup]);
    var handleFileSelect = useCallback(function (name, file) {
        setFiles(function (previousFiles) {
            var nextFiles = __assign({}, previousFiles);
            if (file) {
                nextFiles[name] = file;
            }
            else {
                delete nextFiles[name];
            }
            return nextFiles;
        });
        handleFieldChange(name, file ? file.name : '');
    }, [handleFieldChange]);
    var mergeValues = useCallback(function (partialValues) {
        setValues(function (previousValues) {
            var nextValues = __assign(__assign({}, previousValues), partialValues);
            return deriveValuesRef.current
                ? __assign(__assign({}, nextValues), deriveValuesRef.current(nextValues)) : nextValues;
        });
        setErrors(function (previousErrors) {
            var nextErrors = __assign({}, previousErrors);
            Object.keys(partialValues).forEach(function (key) { return delete nextErrors[key]; });
            return nextErrors;
        });
    }, []);
    mergeValuesRef.current = mergeValues;
    var validateForm = useCallback(function () {
        var nextErrors = validateFormSections(sectionsRef.current, valuesRef.current);
        setErrors(nextErrors);
        setTouched(function (previousTouched) {
            var nextTouched = __assign({}, previousTouched);
            Object.keys(nextErrors).forEach(function (key) {
                nextTouched[key] = true;
            });
            return nextTouched;
        });
        return !hasFormErrors(nextErrors);
    }, []);
    var resetForm = useCallback(function () {
        setValues(__assign({}, initialValuesRef.current));
        setErrors({});
        setTouched({});
        setFiles({});
        lastLookedUpValueRef.current = {};
    }, []);
    var getValues = useCallback(function () { return valuesRef.current; }, []);
    var getFiles = useCallback(function () { return filesRef.current; }, []);
    var isDirty = useMemo(function () {
        return trackDirty &&
            (!deepEqual(values, initialValuesRef.current) || Object.keys(files).length > 0);
    }, [trackDirty, values, files]);
    return {
        values: values,
        errors: errors,
        touched: touched,
        isDirty: isDirty,
        files: files,
        handleFieldChange: handleFieldChange,
        handleFieldBlur: handleFieldBlur,
        handleFileSelect: handleFileSelect,
        mergeValues: mergeValues,
        searchOptions: searchOptions,
        handleSearchInput: handleSearchInput,
        handleSearchSelect: handleSearchSelect,
        validateForm: validateForm,
        resetForm: resetForm,
        getValues: getValues,
        getFiles: getFiles,
    };
};
export default useDynamicForm;
